import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Users,
  CreditCard,
  LogOut,
  MessageSquare,
  Menu,
  X,
  HelpCircle,
  Award,
  FileText,
  BarChart,
  Key,
} from "react-feather";
import {
  Building2,
  Tag,
  Banknote,
  ChevronDown,
  ChevronRight,
  Settings,
  Mail,
  Sparkles,
  Send,
  Rocket,
  Megaphone,
  PanelLeftClose,
  PanelLeftOpen,
  Wallet,
  Target,
  Briefcase,
  Camera,
  Ticket,
  Zap,
  Newspaper,
  BookOpen,
  Store,
  Palette,
  ListOrdered,
} from "lucide-react";
import { useAdminAuth } from "../../hooks/useAdminAuth";
import { ThemeToggle } from "../ThemeToggle";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { useState, useEffect, useMemo } from "react";
import logo from "../../assets/logo.svg";
import { IconMedal, IconTrophy, IconUserStar } from "@tabler/icons-react";
import { AdminRole } from "../../services/admin/admin.service";
import { getLoginPathForRole } from "../../contexts/AdminAuthContext/context";

type MenuItem = { path: string; label: string; icon: React.ReactNode };
type ExpandableMenuItem = {
  key: string;
  label: string;
  icon: React.ReactNode;
  children: MenuItem[];
};

function getRoutePrefix(role: AdminRole): string {
  return role === "SALES" ? "/sales" : "/admin";
}

function getPanelTitle(role: AdminRole): string {
  return role === "SALES" ? "Sales Panel" : "Admin Panel";
}

function getPanelSubtitle(role: AdminRole): string {
  return role === "SALES" ? "Manage your leads" : "Manage your platform";
}

/** Active when the route is the item path or nested under it, so detail
    pages keep their section highlighted. */
function routeMatches(pathname: string, base: string) {
  return pathname === base || pathname.startsWith(base + "/");
}

const ALL_MENU_ITEMS: (MenuItem & { id: string })[] = [
  { id: "dashboard", path: "/dashboard", label: "Dashboard", icon: <Home size={20} /> },
  { id: "onboarding", path: "/onboarding", label: "Onboarding", icon: <Rocket size={20} /> },
  { id: "affiliates", path: "/affiliates", label: "Affiliates", icon: <Users size={20} /> },
  { id: "brands", path: "/brands", label: "Brands", icon: <Building2 size={20} /> },
  { id: "brand-ranking", path: "/brand-ranking", label: "Brand Ranking", icon: <ListOrdered size={20} /> },
  { id: "brand-white-label", path: "/brand-white-label", label: "Brand White-Label", icon: <Palette size={20} /> },
  { id: "plans", path: "/plans", label: "Plans", icon: <CreditCard size={20} /> },
  { id: "social-search-credits", path: "/social-search-credits", label: "Social Search Credits", icon: <Sparkles size={20} /> },
  { id: "bulk-invites", path: "/bulk-invites", label: "Bulk Invite Jobs", icon: <Send size={20} /> },
  { id: "niches", path: "/niches", label: "Niches", icon: <Tag size={20} /> },
  { id: "campaigns", path: "/campaigns", label: "Campaigns", icon: <Megaphone size={20} /> },
  { id: "payouts", path: "/payouts", label: "Payouts", icon: <Wallet size={20} /> },
  { id: "agency-payouts", path: "/agency-payouts", label: "Agency Payouts", icon: <Banknote size={20} /> },
  { id: "support-tickets", path: "/support-tickets", label: "Support Tickets", icon: <MessageSquare size={20} /> },
  { id: "leads", path: "/leads", label: "Leads", icon: <Target size={20} /> },
  { id: "sales-team", path: "/sales-team", label: "Sales Team", icon: <Briefcase size={20} /> },
  { id: "snapshots", path: "/analytics/snapshots", label: "Snapshots", icon: <Camera size={20} /> },
  { id: "landing-page", path: "/analytics/landing-page", label: "Landing Page Analytics", icon: <BarChart size={20} /> },
  { id: "faq", path: "/faq", label: "FAQ", icon: <HelpCircle size={20} /> },
];

const ALL_EXPANDABLE_ITEMS: (ExpandableMenuItem & { id: string; children: (MenuItem & { id: string })[] })[] = [
  {
    id: "invites",
    key: "invites",
    label: "Invites",
    icon: <Ticket size={20} />,
    children: [
      { id: "admin-invites", path: "/invites/admin-invites", label: "Admin Invites", icon: <FileText size={20} /> },
      { id: "trial-otps", path: "/invites/trial-otps", label: "Trial OTPs", icon: <Key size={20} /> },
    ],
  },
  {
    id: "rewards",
    key: "rewards",
    label: "Rewards",
    icon: <IconMedal size={20} />,
    children: [
      { id: "unified", path: "/rewards/unified", label: "All Rewards", icon: <IconTrophy size={20} /> },
      { id: "management", path: "/rewards/management", label: "Affiliate Management", icon: <Settings size={20} /> },
      { id: "brand-management", path: "/rewards/brand-management", label: "Brand Management", icon: <Store size={20} /> },
      { id: "agency-management", path: "/rewards/agency-management", label: "Agency Management", icon: <IconUserStar size={20} /> },
    ],
  },
  {
    id: "automations",
    key: "automations",
    label: "Automations",
    icon: <Zap size={20} />,
    children: [
      { id: "brand-email-automation", path: "/brand-email-automation", label: "Brand Email Automation", icon: <Mail size={20} /> },
      { id: "release-notes", path: "/automations/release-notes", label: "Release Notes", icon: <Newspaper size={20} /> },
      { id: "email-automations", path: "/automations/email-automations", label: "Milestone Configurations", icon: <Award size={20} /> },
      { id: "agency-blog", path: "/automations/agency-blog", label: "Agency Blog", icon: <BookOpen size={20} /> },
    ],
  },
];

const ROLE_ALLOWED_ITEMS: Record<AdminRole, string[]> = {
  ADMIN: ALL_MENU_ITEMS.map((i) => i.id),
  SUPER_ADMIN: ALL_MENU_ITEMS.map((i) => i.id),
  SALES: ["leads"],
};

const ROLE_ALLOWED_EXPANDABLE: Record<AdminRole, string[]> = {
  ADMIN: ALL_EXPANDABLE_ITEMS.map((i) => i.id),
  SUPER_ADMIN: ALL_EXPANDABLE_ITEMS.map((i) => i.id),
  SALES: [],
};

/** Collapsed rail: expandable items open a hover flyout so their children
    stay reachable without expanding the sidebar. */
function CollapsedExpandableItem({
  item,
  pathname,
}: {
  item: ExpandableMenuItem;
  pathname: string;
}) {
  const [open, setOpen] = useState(false);
  const hasActiveChild = item.children.some((child) =>
    routeMatches(pathname, child.path)
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          className={`w-full flex items-center justify-center px-3 py-3 rounded-xl mb-1 transition-all duration-200 ${
            hasActiveChild
              ? "bg-orange-50 dark:bg-orange-500/10 text-[#d7785b]"
              : "text-muted-foreground hover:bg-accent hover:text-foreground"
          }`}
          title={item.label}
        >
          <span className="flex-shrink-0">{item.icon}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        sideOffset={8}
        className="w-60 !p-2"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <p className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {item.label}
        </p>
        <div className="space-y-1 border-t border-border pt-1">
          {item.children.map((child) => (
            <Link
              key={child.path}
              to={child.path}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                routeMatches(pathname, child.path)
                  ? "bg-orange-50 dark:bg-orange-500/10 text-[#d7785b]"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              }`}
            >
              <span className="flex-shrink-0">{child.icon}</span>
              <span className="font-medium">{child.label}</span>
            </Link>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function AdminSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAdminAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const role: AdminRole = user?.role || "ADMIN";
  const prefix = getRoutePrefix(role);

  const menuItems = useMemo(() => {
    const allowedIds = ROLE_ALLOWED_ITEMS[role] || [];
    return ALL_MENU_ITEMS
      .filter((item) => allowedIds.includes(item.id))
      .map((item) => ({ ...item, path: `${prefix}${item.path}` }));
  }, [role, prefix]);

  const expandableMenuItems = useMemo(() => {
    const allowedIds = ROLE_ALLOWED_EXPANDABLE[role] || [];
    return ALL_EXPANDABLE_ITEMS
      .filter((item) => allowedIds.includes(item.id))
      .map((item) => ({
        ...item,
        children: item.children.map((child) => ({ ...child, path: `${prefix}${child.path}` })),
      }));
  }, [role, prefix]);

  const handleLogout = () => {
    const loginPath = getLoginPathForRole(role);
    logout();
    navigate(loginPath);
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Auto-expand menu items when their children are active
  useEffect(() => {
    const activeExpandableItem = expandableMenuItems.find((item) =>
      item.children.some((child) => routeMatches(location.pathname, child.path))
    );

    if (
      activeExpandableItem &&
      !expandedItems.includes(activeExpandableItem.key)
    ) {
      setExpandedItems((prev) => [...prev, activeExpandableItem.key]);
    }
  }, [location.pathname, expandableMenuItems, expandedItems]);

  const toggleExpanded = (key: string) => {
    setExpandedItems((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  };

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-card border border-border shadow-sm hover:shadow-md transition-all duration-200"
      >
        {isMobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed lg:relative top-0 left-0 h-screen overflow-y-auto overflow-x-hidden scrollbar-hide bg-card border-r border-border z-50
          transition-all duration-300 ease-in-out
          ${
            isMobileOpen
              ? "translate-x-0"
              : "-translate-x-full lg:translate-x-0"
          }
          ${isCollapsed ? "lg:w-20" : "lg:w-72"}
          w-72
        `}
      >
        {/* Header */}
        <div
          className={`border-b border-border ${
            isCollapsed ? "p-4" : "p-6"
          } transition-all duration-300`}
        >
          {/* Collapsed: stack logo above the toggle so the 80px rail never
              overflows horizontally. */}
          <div
            className={`flex items-center ${
              isCollapsed ? "flex-col gap-3" : "justify-between"
            }`}
          >
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-card rounded-lg border border-border flex items-center justify-center shadow-sm">
                <img src={logo} alt="Reveshare" className="w-5 h-5 dark:invert" />
              </div>
              {!isCollapsed && (
                <div className="transition-all duration-300">
                  <h1 className="text-lg font-semibold text-foreground">
                    {getPanelTitle(role)}
                  </h1>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {getPanelSubtitle(role)}
                  </p>
                </div>
              )}
            </div>

            {/* Desktop Collapse Button */}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="hidden lg:flex p-1.5 rounded-lg hover:bg-accent transition-all duration-200"
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? (
                <PanelLeftOpen size={16} className="text-muted-foreground" />
              ) : (
                <PanelLeftClose size={16} className="text-muted-foreground" />
              )}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto scrollbar-hide">
          <div
            className={`${
              isCollapsed ? "px-2 py-4" : "px-3 py-4"
            } transition-all duration-300`}
          >
            {/* Regular Menu Items */}
            {menuItems.map((item) => {
              const isItemActive = routeMatches(location.pathname, item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    flex items-center transition-all duration-200 rounded-xl mb-1
                    ${isCollapsed ? "px-3 py-3 justify-center" : "px-4 py-3 border-l-4"}
                    ${
                      isItemActive
                        ? `bg-orange-50 dark:bg-orange-500/10 text-[#d7785b] ${isCollapsed ? "" : "border-[#d7785b]"}`
                        : `text-muted-foreground hover:bg-accent hover:text-foreground ${isCollapsed ? "" : "border-transparent"}`
                    }
                  `}
                  title={isCollapsed ? item.label : ""}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  {!isCollapsed && (
                    <span className="ml-3 font-medium transition-all duration-300">
                      {item.label}
                    </span>
                  )}
                </Link>
              );
            })}

            {/* Expandable Menu Items */}
            {expandableMenuItems.map((item) => {
              if (isCollapsed) {
                return (
                  <CollapsedExpandableItem
                    key={item.key}
                    item={item}
                    pathname={location.pathname}
                  />
                );
              }

              const isExpanded = expandedItems.includes(item.key);
              const hasActiveChild = item.children.some((child) =>
                routeMatches(location.pathname, child.path)
              );

              return (
                <div key={item.key} className="mb-1">
                  {/* Parent Item */}
                  <button
                    onClick={() => toggleExpanded(item.key)}
                    className={`
                      w-full flex items-center transition-all duration-200 rounded-xl px-4 py-3 border-l-4
                      ${
                        hasActiveChild
                          ? "bg-orange-50 dark:bg-orange-500/10 text-[#d7785b] border-[#d7785b]"
                          : "text-muted-foreground hover:bg-accent hover:text-foreground border-transparent"
                      }
                    `}
                  >
                    <span className="flex-shrink-0">{item.icon}</span>
                    <span className="ml-3 font-medium transition-all duration-300 flex-1 text-left">
                      {item.label}
                    </span>
                    {isExpanded ? (
                      <ChevronDown size={16} className="text-muted-foreground" />
                    ) : (
                      <ChevronRight size={16} className="text-muted-foreground" />
                    )}
                  </button>

                  {/* Children Items */}
                  {isExpanded && (
                    <div className="ml-6 mt-1 space-y-1">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`
                            flex items-center transition-all duration-200 rounded-lg
                            px-3 py-2 text-sm
                            ${
                              routeMatches(location.pathname, child.path)
                                ? "bg-orange-50 dark:bg-orange-500/10 text-[#d7785b]"
                                : "text-muted-foreground hover:bg-accent hover:text-foreground"
                            }
                          `}
                        >
                          <span className="flex-shrink-0 mr-2">
                            {child.icon}
                          </span>
                          <span className="font-medium">{child.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Logout Section */}
          <div
            className={`border-t border-border ${
              isCollapsed ? "p-2" : "p-4"
            } transition-all duration-300`}
          >
            {/* Expanded: logout + theme toggle share a row. Collapsed: the
                80px rail only fits them stacked. */}
            <div
              className={`flex items-center ${
                isCollapsed ? "flex-col gap-1" : "gap-2"
              }`}
            >
              <button
                onClick={handleLogout}
                className={`
                  flex items-center text-muted-foreground hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10 dark:hover:text-red-400
                  rounded-xl transition-all duration-200
                  ${isCollapsed ? "w-full px-3 py-3 justify-center" : "flex-1 px-4 py-3"}
                `}
                title={isCollapsed ? "Logout" : ""}
              >
                <LogOut size={20} />
                {!isCollapsed && (
                  <span className="ml-3 font-medium transition-all duration-300">
                    Logout
                  </span>
                )}
              </button>
              <ThemeToggle className="h-8 w-8 shrink-0" />
            </div>
          </div>
        </nav>
      </div>
    </>
  );
}
